"use client";

import * as React from "react";
import Section, { Reveal } from "../layout/Section";
import { SITE, email, mailto } from "../../data/site";

type Status = "idle" | "sending" | "sent" | "error";

export default function Contact() {
  const [status, setStatus] = React.useState<Status>("idle");
  const [error, setError] = React.useState("");

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = Object.fromEntries(new FormData(form)) as Record<
      string,
      string
    >;

    if (!data.name || data.name.trim().length < 2)
      return fail("Please enter your name.");
    if (!data.email || !/.+@.+\..+/.test(data.email))
      return fail("Please enter a valid email address.");
    if (!data.message || data.message.trim().length < 10)
      return fail("A little more detail would help.");

    setStatus("sending");
    setError("");

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const type = res.headers.get("content-type") || "";
      const body = type.includes("application/json")
        ? await res.json().catch(() => ({}))
        : {};

      if (!res.ok || body?.ok === false) {
        return fail(
          body?.error || `Something went wrong on my end (${res.status}).`,
        );
      }
      setStatus("sent");
      form.reset();
    } catch (err) {
      fail(
        err instanceof Error
          ? err.message
          : "Network error. Try email instead?",
      );
    }
  }

  function fail(message: string) {
    setStatus("error");
    setError(message);
  }

  return (
    <Section
      id="contact"
      index="04"
      title="Contact"
      lede="If your team works on problems like these, I would like to hear about it."
    >
      <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:gap-16">
        <Reveal>
          <div className="space-y-8">
            <div>
              <p className="eyebrow">Direct</p>
              <a
                href={mailto()}
                className="link mt-2 inline-block text-lg tracking-tight"
              >
                {email()}
              </a>
            </div>

            <div>
              <p className="eyebrow">Elsewhere</p>
              <ul className="mt-3 space-y-2">
                <ContactLink
                  href={SITE.links.linkedin}
                  label="LinkedIn"
                  hint="jaehan-loh"
                />
                <ContactLink
                  href={SITE.links.github}
                  label="GitHub"
                  hint="jaehan98"
                />
                <ContactLink
                  href={SITE.links.resume}
                  label="Resume"
                  hint="PDF"
                />
              </ul>
            </div>

            <div className="panel-quiet p-4">
              <p className="text-2xs leading-relaxed text-faint">
                {SITE.availabilityLong} Based in {SITE.location}; remote,
                hybrid, or relocation for the right team.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={80}>
          <form onSubmit={onSubmit} className="panel p-5 md:p-6">
            <input
              type="text"
              name="botfield"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden
              className="hidden"
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <Field id="name" label="Name">
                <input
                  id="name"
                  name="name"
                  required
                  autoComplete="name"
                  placeholder="Your name"
                  className="field"
                />
              </Field>
              <Field id="email" label="Email">
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="you@company.com"
                  className="field"
                />
              </Field>
            </div>

            <div className="mt-4">
              <Field id="message" label="Message">
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={4000}
                  placeholder="What are you working on?"
                  className="field resize-y"
                />
              </Field>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={status === "sending"}
              >
                {status === "sending" ? "Sending…" : "Send message"}
              </button>

              <p aria-live="polite" className="text-sm">
                {status === "sent" && (
                  <span className="text-emerald-600 dark:text-emerald-400">
                    Sent. I&rsquo;ll get back to you shortly.
                  </span>
                )}
                {status === "error" && (
                  <span className="text-rose-600 dark:text-rose-400">
                    {error}
                  </span>
                )}
              </p>
            </div>
          </form>
        </Reveal>
      </div>
    </Section>
  );
}

function Field({
  id,
  label,
  children,
}: React.PropsWithChildren<{ id: string; label: string }>) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-2xs font-medium text-muted">
        {label}
      </label>
      {children}
    </div>
  );
}

function ContactLink({
  href,
  label,
  hint,
}: {
  href: string;
  label: string;
  hint: string;
}) {
  return (
    <li>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="group flex items-baseline gap-2 no-underline"
      >
        <span className="link text-sm">{label}</span>
        <span className="font-mono text-2xs text-faint">{hint}</span>
        <span className="text-faint transition-transform duration-300 group-hover:translate-x-0.5">
          <svg width="11" height="11" viewBox="0 0 16 16" aria-hidden>
            <path
              d="M5.5 10.5 10.5 5.5M6.5 5.5h4v4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </a>
    </li>
  );
}
